import {observer} from "mobx-react";
import BillingTransactionStore from "../../stores/billing-transaction-store";
import ProcessingIndicator from "./processing-indicator/processing-indicator";
import FontBody1 from "../../../../components/fonts/font-body1/font-body1";
import {FONT_COLOR, FONT_VARIANT} from "../../../../components/fonts/font-props";
import {Note, NOTE_TYPE} from "../../../../components/note/note";
import FontBody2 from "../../../../components/fonts/font-body2/font-body2";
import {decimalFormat} from "../../../../services/helpers";
import {Tooltip} from "../../../../components/tooltip/tooltip";
import TooltipInfoLayout from "./layouts/tooltip-info-layout/tooltip-info-layout";
import Icon from "../../../../components/icons/icon/icon";
import IconInfo from "../../../../components/icons/icon-info/icon-info";
import BillingDetailsContentRow from "./layouts/billing-details-content-row/billing-details-content-row";
import FontBody4 from "../../../../components/fonts/font-body4/font-body4";
import {PLAN_CHARGE_TYPES} from "../../../manage-subscription-plans/stores/subscription-plan-editor-store";
import {Badge, BadgeType} from "../../../../components/badge/badge";
import ChargedAmount from "../charged-amount/charged-amount";
import {EXCESS_USAGE_STATUS} from "../../../../services/api/billing-service";
import FontHeading4 from "../../../../components/fonts/font-heading4/font-heading4";
import {getPartnerLogo} from "./details-modal";

interface IParentModalContent {
    transaction: BillingTransactionStore;
}

const ParentModalContent: React.FC<IParentModalContent> = observer(({transaction}) => {

    const shouldShowChildren = transaction.childUsages && transaction.childUsages?.length > 0;

    return (
        <>
            <div className="billing-details-modal-content">
                <ProcessingIndicator isProcessing={transaction.prev_period_usages_processing}/>
                <div className="content-header">
                    <FontBody1 color={FONT_COLOR.NEUTRAL3}>Billing details - {transaction.month}</FontBody1>
                    {transaction.partnerId && getPartnerLogo(transaction.partnerId)}
                </div>
                {transaction.prev_period_parent_usage && (
                    <Note type={NOTE_TYPE.INFORMATION} body={
                        <div className="prev-period-usage">
                            <span>Previous billing period</span>
                            <span className="prev-total-amount">
                                <FontBody2
                                    variant={FONT_VARIANT.BOLD}>${decimalFormat(transaction.prev_period_parent_usage.total)}</FontBody2>
                                <Tooltip
                                    content={<TooltipInfoLayout>
                                        <span>Plan payments subtotal - ${decimalFormat(transaction.prev_period_parent_usage.plan_payments_total)}</span>
                                        <span>Add-ons payments subtotal - ${decimalFormat(transaction.prev_period_parent_usage.plan_addons_total)}</span>
                                    </TooltipInfoLayout>}
                                >
                                    <Icon Component={IconInfo}/>
                                </Tooltip>
                            </span>
                        </div>
                    }/>
                )}
                <div className="modal-details-heading">
                    <FontBody4 variant={[FONT_VARIANT.BOLD, FONT_VARIANT.UPPERCASE]}
                               color={FONT_COLOR.PRIMARY2}>plan</FontBody4>
                    <FontBody2
                        variant={FONT_VARIANT.BOLD}>{transaction.plan_name}{transaction.planType === PLAN_CHARGE_TYPES.ANNUAL &&
                        <Badge type={BadgeType.INFO} text="Annual"></Badge>}</FontBody2>
                </div>
                <BillingDetailsContentRow>
                    <FontBody2>Plan price</FontBody2>
                    <div></div>
                    <div></div>
                    <FontBody2>${transaction.parentPlanPrice ? decimalFormat(transaction.parentPlanPrice) : decimalFormat(transaction.planPrice)}</FontBody2>
                </BillingDetailsContentRow>
                <BillingDetailsContentRow>
                    <FontBody2>{transaction.planType === PLAN_CHARGE_TYPES.ANNUAL ? 'Yearly' : 'Monthly'} returns
                        quota</FontBody2>
                    <FontBody2 variant={FONT_VARIANT.BOLD}>{transaction.rmaQuota}</FontBody2>
                </BillingDetailsContentRow>
                <BillingDetailsContentRow>
                    <FontBody2># of excess returns</FontBody2>
                    <FontBody2 variant={FONT_VARIANT.BOLD}>{transaction.excessRmas}</FontBody2>
                    <FontBody2
                        variant={FONT_VARIANT.BOLD}>${transaction.pricePerExtraRma !== undefined ? decimalFormat(transaction.pricePerExtraRma) : 'N/A'}</FontBody2>
                    {transaction.excessRmaCharge !== undefined ? (
                        <ChargedAmount
                            value={`$${decimalFormat(transaction.excessRmaCharge)}`}
                            notCharged={transaction.parentHasCharge && transaction.status === EXCESS_USAGE_STATUS.NOT_CHARGED}
                        />
                    ) : 'N/A'}
                </BillingDetailsContentRow>
                {shouldShowChildren &&
                    <>
                        <div className="child-excess-usage-row">
                            <FontBody2 variant={FONT_VARIANT.BOLD}>
                                quota used by :
                            </FontBody2>
                        </div>

                        <BillingDetailsContentRow>
                            <div className="child-excess-usage-row">
                                <FontBody2>{transaction.billedUser?.name}</FontBody2>
                            </div>
                            <FontBody2 variant={FONT_VARIANT.BOLD}>{transaction.rmaCount}</FontBody2>
                        </BillingDetailsContentRow>

                        {transaction.childUsages?.map((childUsage) =>
                            <BillingDetailsContentRow key={childUsage.id}>
                                <div className="child-excess-usage-row">
                                    <FontBody2>{childUsage?.billed_user?.name}</FontBody2>
                                </div>
                                <FontBody2 variant={FONT_VARIANT.BOLD}>{childUsage.rma_count}</FontBody2>
                            </BillingDetailsContentRow>
                        )}
                    </>
                }
                <BillingDetailsContentRow>
                    <FontBody2>{transaction.planType === PLAN_CHARGE_TYPES.ANNUAL ? 'Yearly' : 'Monthly'} order tracking
                        quota</FontBody2>
                    <FontBody2 variant={FONT_VARIANT.BOLD}>{transaction.orderTrackingQuota}</FontBody2>
                </BillingDetailsContentRow>
                <BillingDetailsContentRow>
                    <FontBody2># of excess order tracking</FontBody2>
                    <FontBody2 variant={FONT_VARIANT.BOLD}>{transaction.excessOrderTrackingCount}</FontBody2>
                    <FontBody2
                        variant={FONT_VARIANT.BOLD}> ${transaction.pricePerExcessOrderTracking !== undefined ? decimalFormat(transaction.pricePerExcessOrderTracking) : 'N/A'} </FontBody2>
                    {transaction.excessOrderTrackingCharge !== undefined ? (
                        <ChargedAmount
                            value={`$${decimalFormat(transaction.excessOrderTrackingCharge)}`}
                            notCharged={transaction.parentHasCharge && transaction.status === EXCESS_USAGE_STATUS.NOT_CHARGED}
                        />
                    ) : 'N/A'}
                </BillingDetailsContentRow>
                {shouldShowChildren &&
                    <>
                        <div className="child-excess-usage-row">
                            <FontBody2 variant={FONT_VARIANT.BOLD}>quota used by :</FontBody2>
                        </div>
                        <BillingDetailsContentRow>
                            <div className="child-excess-usage-row">
                                <FontBody2>{transaction.billedUser?.name}</FontBody2>
                            </div>
                            <FontBody2 variant={FONT_VARIANT.BOLD}>{transaction.orderTrackingCount}</FontBody2>
                        </BillingDetailsContentRow>
                        {transaction.childUsages?.map((childUsage) =>
                            <BillingDetailsContentRow key={childUsage.id}>
                                <div className="child-excess-usage-row">
                                    <FontBody2>{childUsage?.billed_user?.name}</FontBody2>
                                </div>
                                <FontBody2 variant={FONT_VARIANT.BOLD}>{childUsage.order_tracking_count}</FontBody2>

                            </BillingDetailsContentRow>
                        )}
                    </>
                }
                {((transaction.planType === PLAN_CHARGE_TYPES.ANNUAL && transaction.isFirstMonthOfAnnualPlan) ||
                    transaction.planType === PLAN_CHARGE_TYPES.RECURRING) && (
                    <BillingDetailsContentRow className="subtotal">
                        <div></div>
                        <div></div>
                        <FontBody2>Subtotal</FontBody2>
                        <FontBody2
                            variant={FONT_VARIANT.BOLD}>${decimalFormat(transaction.plan_payments_total)}</FontBody2>
                    </BillingDetailsContentRow>
                )}
                {!!transaction.plan_addons.length && (
                    <>
                        <FontBody4 variant={[FONT_VARIANT.BOLD, FONT_VARIANT.UPPERCASE]} color={FONT_COLOR.PRIMARY2}>Addons
                            included:</FontBody4>
                        {transaction.plan_addons.map(pa => {
                            return (
                                <BillingDetailsContentRow key={pa.display_name}>
                                    <div className="addon-name">
                                        <FontBody2>{pa.display_name}</FontBody2>
                                    </div>
                                    <div></div>
                                    <FontBody2>${decimalFormat(pa.price)}</FontBody2>
                                </BillingDetailsContentRow>
                            );
                        })}
                        <BillingDetailsContentRow className="subtotal">
                            <div></div>
                            <div></div>
                            <FontBody2>Subtotal</FontBody2>
                            <FontBody2
                                variant={FONT_VARIANT.BOLD}>${decimalFormat(transaction.plan_addons_total)}</FontBody2>
                        </BillingDetailsContentRow>
                    </>
                )}
                <BillingDetailsContentRow className="total">
                    <div></div>
                    <div></div>
                    <FontHeading4 variant={FONT_VARIANT.BOLD}>Total</FontHeading4>
                    <FontHeading4 variant={FONT_VARIANT.BOLD}>${decimalFormat(transaction.total)}</FontHeading4>
                </BillingDetailsContentRow>
            </div>
        </>
    );
});

export default ParentModalContent
